import { Box, Flex, Spinner } from '@chakra-ui/react';
import { ErrorDetails } from '@texas/components/shared/alert/ErrorDetails';
import { BodyOverride } from '@texas/components/shared/BodyOverride';
import { EntityDirectoryPage } from '@texas/components/shared/entityDirectory/EntityDirectoryPage';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { ArticleWarningSummary } from '../ArticleWarningSummary';
import { useTranslation } from 'react-i18next';
import { CreateArticleAction } from './CreateArticleAction';
import { CreateArticleBranches } from './CreateArticleBranches';
import { CreateArticleConfiguration } from './CreateArticleConfiguration';
import { CreateArticleInformation } from './CreateArticleInformation';
import { CreateArticleProposals } from './CreateArticleProposals';
import { CreateArticleSummary } from './CreateArticleSummary';
import { useWarningNavigation } from '../useWarningNavigation';
import { CreateArticleProvider, useCreateArticle } from './useCreateArticle';

interface Props {
  customerId?: number;
  conceptId?: number;
}
export function CreateArticlePage(props: Props) {
  return (
    <CreateArticleProvider {...props}>
      <CreateArticleContent />
    </CreateArticleProvider>
  );
}

function CreateArticleContent() {
  const { t } = useTranslation();
  const createArticle = useCreateArticle();
  const navigateToWarning = useWarningNavigation(
    createArticle.revealWarningGroup,
  );
  return (
    <BodyOverride>
      <EntityDirectoryPage
        sx={styles.page}
        title={t('article.create.title')}
        description={t('article.create.description')}
      >
        <Box sx={styles.columns}>
          <Box sx={styles.stack}>
            {createArticle.loading && <Spinner />}
            {createArticle.errors.map((error, i) => (
              <ErrorDetails key={i} error={error} />
            ))}
            <Box sx={styles.sections}>
              <CreateArticleInformation />
              <CreateArticleConfiguration />
              <CreateArticleProposals />
              <CreateArticleBranches />
            </Box>
          </Box>
          <Flex
            as="aside"
            aria-label={t('article.create.summary')}
            direction="column"
            position={{ lg: 'sticky' }}
            top={4}
            maxH={{ lg: 'calc(100dvh - 144px)' }}
            minW={0}
            minH={0}
          >
            <ArticleWarningSummary
              warnings={createArticle.warningItems}
              onNavigate={navigateToWarning}
            />
            <Box sx={styles.summary} position="static" maxH="none">
              <CreateArticleSummary />
              <Box sx={styles.summaryActions}>
                {createArticle.creationRequest.error && (
                  <ErrorDetails error={createArticle.creationRequest.error} />
                )}
                <CreateArticleAction />
              </Box>
            </Box>
          </Flex>
        </Box>
      </EntityDirectoryPage>
    </BodyOverride>
  );
}
